import {
  Box,
  Button,
  Code,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  useToast,
  VisuallyHidden,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdContentCopy, MdDownload } from 'react-icons/md';
import { copy } from '../copy';
import { ExitUnsavedKeyDialog } from './ExitUnsavedKeyDialog';

type Props = {
  isOpen: boolean;
  plaintext: string | null;
  keyName: string;
  onClose: () => void;
};

/**
 * The ceremonial moment. The key is shown exactly once. Close button stays
 * disabled until the user has either copied OR downloaded — no 10-second timer
 * unlock (hostile to screen readers and to users who just stopped reading).
 *
 * ESC / overlay click / close button all trigger `ExitUnsavedKeyDialog` if the
 * user hasn't stored the key yet. No emoji. Amber border = consequential, not
 * broken.
 */
export function NewApiKeySuccessModal({
  isOpen,
  plaintext,
  keyName,
  onClose,
}: Props) {
  const [stored, setStored] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const announceRef = useRef<HTMLSpanElement>(null);
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setStored(false);
      setCopied(false);
      setDownloaded(false);
      setShowExitConfirm(false);
    }
  }, [isOpen]);

  const announce = (msg: string) => {
    if (announceRef.current) announceRef.current.textContent = msg;
  };

  const handleCopy = useCallback(async () => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setStored(true);
      announce(copy.success.ariaAnnounce);
      setTimeout(() => doneButtonRef.current?.focus(), 50);
    } catch {
      // Clipboard API may fail (insecure context, Safari private). Select the
      // text so the user can Cmd+C manually.
      const el = document.getElementById('api-key-plaintext');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      toast({
        title: copy.success.clipboardFailed,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
    }
  }, [plaintext, toast]);

  const handleDownload = useCallback(() => {
    if (!plaintext) return;
    const blob = new Blob([plaintext + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = keyName.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
    a.download = `mysportset-api-key-${safeName || 'key'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);
    setStored(true);
  }, [plaintext, keyName]);

  const tryClose = () => {
    if (stored) {
      onClose();
    } else {
      setShowExitConfirm(true);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={tryClose}
        size="xl"
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" p={6}>
          <ModalBody>
            <Heading
              fontFamily="'Instrument Serif', Georgia, serif"
              fontSize="28px"
              fontWeight={400}
              mb={3}
            >
              {copy.success.headline}
            </Heading>
            <Text fontSize="15px" color="gray.700" mb={6}>
              {copy.success.body}
            </Text>

            <Box
              border="2px solid"
              borderColor="orange.300"
              borderRadius="12px"
              bg="orange.50"
              p={4}
              mb={4}
            >
              <Code
                id="api-key-plaintext"
                display="block"
                whiteSpace="pre-wrap"
                wordBreak="break-all"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="13px"
                bg="transparent"
                p={0}
              >
                {plaintext ?? ''}
              </Code>
            </Box>

            <Flex gap={3} mb={4} wrap="wrap">
              <Button
                leftIcon={<MdContentCopy />}
                colorScheme="brand"
                onClick={handleCopy}
                isDisabled={!plaintext}
              >
                {copied ? copy.success.copiedButton : copy.success.copyButton}
              </Button>
              <Button
                leftIcon={<MdDownload />}
                variant="ghost"
                onClick={handleDownload}
                isDisabled={!plaintext}
              >
                {downloaded
                  ? copy.success.downloadedButton
                  : copy.success.downloadButton}
              </Button>
            </Flex>

            <Text fontSize="13px" color="gray.600">
              {copy.success.footer}
            </Text>

            <VisuallyHidden role="status" aria-live="polite">
              <span ref={announceRef} />
            </VisuallyHidden>
          </ModalBody>
          <ModalFooter>
            <Button
              ref={doneButtonRef}
              onClick={tryClose}
              colorScheme={stored ? 'brand' : 'gray'}
              isDisabled={!stored}
              title={
                stored ? undefined : copy.success.doneDisabledTooltip
              }
            >
              {copy.success.done}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ExitUnsavedKeyDialog
        isOpen={showExitConfirm}
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={() => {
          setShowExitConfirm(false);
          onClose();
        }}
      />
    </>
  );
}
